import {
  getOrgAgentsByAgentIdHistoryInfiniteQueryKey,
  patchOrgAgentsByAgentIdBuilderMutation,
  postOrgAgentsByAgentIdBuilderRestoreMutation,
  postOrgAgentsByAgentIdBuilderSuggestionsAcceptMutation,
  postOrgAgentsByAgentIdBuilderSuggestionsRejectMutation,
} from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { trackAction } from '@onefootprint/logger';
import type {
  AgentBuilderHistory,
  AgentBuilderResponse,
  AgentGraphOperation,
} from '@onefootprint/request-types/dashboard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type Dispatch, useRef, useState } from 'react';
import useCanEditAgent from '@/hooks/use-can-edit-agent';
import type { Action } from '../store/reducer';
import type { SessionMeta } from '../store/types';

export type MutateOp = (op: AgentGraphOperation) => void;
export type Restore = (direction: 'previous' | 'next') => Promise<AgentBuilderResponse | null>;
export type AcceptSuggestions = (suggestionIds: string[]) => void;
export type RejectSuggestions = (suggestionIds: string[]) => void;

type Args = {
  getSession: () => SessionMeta | null;
  getHistory: () => AgentBuilderHistory;
  dispatch: Dispatch<Action>;
};

const useBuilderMutation = ({ getSession, getHistory, dispatch }: Args) => {
  const canEdit = useCanEditAgent();
  const showErrorToast = useRequestErrorToast();
  const queryClient = useQueryClient();
  const patch = useMutation(patchOrgAgentsByAgentIdBuilderMutation());
  const restore = useMutation(postOrgAgentsByAgentIdBuilderRestoreMutation());
  const acceptBatch = useMutation(postOrgAgentsByAgentIdBuilderSuggestionsAcceptMutation());
  const rejectBatch = useMutation(postOrgAgentsByAgentIdBuilderSuggestionsRejectMutation());

  // Serial queue keeps PATCHes from racing on expectedHeadId. headIdRef updates
  // synchronously in applyResponse so the next queued op reads the fresh head
  // without waiting for a re-render. Dirty-slice preservation lives in the
  // reducer (applyBackendResponse) — this hook only sequences I/O.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const headIdRef = useRef<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  const applyResponse = (response: AgentBuilderResponse) => {
    headIdRef.current = response.current.id;
    dispatch({ type: 'applyBackendResponse', response });
    queryClient.invalidateQueries({
      queryKey: getOrgAgentsByAgentIdHistoryInfiniteQueryKey({ path: { agentId: response.agentId } }),
    });
  };

  const enqueue = <T>(work: (session: SessionMeta) => Promise<T>): Promise<T | null> => {
    if (!canEdit) return Promise.resolve(null);
    setPendingCount(prev => prev + 1);
    const next = queueRef.current.then(async () => {
      const session = getSession();
      if (!session) return null;
      try {
        return await work(session);
      } catch (error) {
        showErrorToast(error);
        return null;
      }
    });
    queueRef.current = next
      .catch(() => undefined)
      .finally(() => {
        setPendingCount(prev => Math.max(0, prev - 1));
      });
    return next;
  };

  const mutateOp: MutateOp = op => {
    enqueue(async session => {
      const response = await patch.mutateAsync({
        path: { agentId: session.agentId },
        body: { operation: op, expectedHeadId: headIdRef.current ?? session.agentVersionId },
      });
      applyResponse(response);
    });
  };

  const restoreTo = (id: string) => {
    return enqueue(async session => {
      const response = await restore.mutateAsync({
        path: { agentId: session.agentId },
        body: { id, expectedHeadId: headIdRef.current ?? session.agentVersionId },
      });
      applyResponse(response);
      return response;
    });
  };

  const restoreDraft: Restore = direction => {
    const target = getHistory()[direction];
    return target ? restoreTo(target.id) : Promise.resolve(null);
  };

  const acceptSuggestions: AcceptSuggestions = suggestionIds => {
    enqueue(async session => {
      const expectedHeadId = headIdRef.current ?? session.agentVersionId;
      const response = await acceptBatch.mutateAsync({
        path: { agentId: session.agentId },
        body: { suggestionIds, expectedHeadId },
      });
      applyResponse(response);
      trackAction('agent_suggestions_accepted', { agent_id: session.agentId, count: suggestionIds.length });
    });
  };

  const rejectSuggestions: RejectSuggestions = suggestionIds => {
    enqueue(async session => {
      const response = await rejectBatch.mutateAsync({
        path: { agentId: session.agentId },
        body: { suggestionIds },
      });
      applyResponse(response);
      trackAction('agent_suggestions_rejected', { agent_id: session.agentId, count: suggestionIds.length });
    });
  };

  return {
    mutateOp,
    restore: restoreDraft,
    discard: restoreTo,
    acceptSuggestions,
    rejectSuggestions,
    applyResponse,
    getHeadId: () => {
      return headIdRef.current;
    },
    flushPendingOps: async () => {
      await queueRef.current;
    },
    isPending: pendingCount > 0,
  };
};

export default useBuilderMutation;
